interface TrailingCoalescer {
    schedule: () => void
    cancel: () => void
    isPending: () => boolean
}

export const createTrailingCoalescer = ({
    windowMs,
    jitterMs = 0,
    random = Math.random,
    run,
}: {
    windowMs: number
    jitterMs?: number
    random?: () => number
    run: () => void
}): TrailingCoalescer => {
    let timer: ReturnType<typeof setTimeout> | null = null
    return {
        schedule: () => {
            if (timer !== null) return
            const delay = windowMs + random() * jitterMs
            timer = setTimeout(() => {
                timer = null
                run()
            }, delay)
        },
        cancel: () => {
            if (timer === null) return
            clearTimeout(timer)
            timer = null
        },
        isPending: () => timer !== null,
    }
}

export const createCoalescerRegistry = <K extends object>() => {
    const byKey = new WeakMap<K, TrailingCoalescer>()
    return {
        forKey: ({
            key,
            windowMs,
            jitterMs,
            random,
            run,
        }: {
            key: K
            windowMs: number
            jitterMs?: number
            random?: () => number
            run: () => void
        }): TrailingCoalescer => {
            const existing = byKey.get(key)
            if (existing) return existing
            const created = createTrailingCoalescer({
                windowMs,
                jitterMs,
                random,
                run,
            })
            byKey.set(key, created)
            return created
        },
    }
}

export const createInFlightCollapser = <Params, Result>({
    keyOf,
    run,
}: {
    keyOf: (params: Params) => string
    run: (params: Params) => Promise<Result>
}): ((params: Params) => Promise<Result>) => {
    const inFlight = new Map<string, Promise<Result>>()
    return params => {
        const key = keyOf(params)
        const joined = inFlight.get(key)
        if (joined) return joined
        const running = run(params).finally(() => inFlight.delete(key))
        inFlight.set(key, running)
        return running
    }
}
